import React, { useEffect, useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import ShippingForm from '../components/checkout/ShippingForm';
import PaymentForm from '../components/checkout/PaymentForm';
import OrderSummary from '../components/checkout/OrderSummary';
import LoginModal from '../components/auth/LoginModal';
import { useCart } from '../context/CartContext';
import { useAuth } from '../context/AuthContext';
import { fadeIn } from '../utils/gsapAnimations';
import Button from '../components/common/Button';

function Checkout() {
  const pageRef = useRef(null);
  const navigate = useNavigate();

  const { cart, getTotal, clearCart } = useCart();
  const { user } = useAuth();

  const total = getTotal();

  const [useAccountDetails, setUseAccountDetails] = useState(false);
  const [showLoginModal, setShowLoginModal] = useState(false);
  const [shippingAddress, setShippingAddress] = useState(null);
  const [isShippingValid, setIsShippingValid] = useState(false);

  useEffect(() => {
    fadeIn(pageRef.current, 0.4);
  }, []);

  useEffect(() => {
    if (user && useAccountDetails) {
      const tenant = user.tenant || {};

const accountShipping = {
  name: tenant.full_name || user.name || '',
  email: tenant.email || user.email || '',
  phone: tenant.telephone || '',
  address: tenant.address || '',
  country: tenant.country?.name || '',
  country_id: tenant.country?.id || '',
  state: tenant.state?.name || '',
  state_id: tenant.state?.id || '',
};

      setShippingAddress(accountShipping);
      setIsShippingValid(
        Boolean(
          accountShipping.name &&
            accountShipping.email &&
            accountShipping.phone &&
            accountShipping.address
        )
      );
    }

    if (!useAccountDetails) {
      setShippingAddress(null);
      setIsShippingValid(false);
    }
  }, [user, useAccountDetails]);

  const handleShippingSubmit = (address, isValid) => {
    setShippingAddress(address);
    setIsShippingValid(isValid);
  };

  const handlePaymentSuccess = (transactionData) => {
    localStorage.setItem(
      'lastSuccessfulOrder',
      JSON.stringify({
        transaction: transactionData,
        shippingAddress,
        cartSnapshot: cart,
      })
    );

    clearCart();

    navigate('/order-confirmation', {
      state: {
        transaction: {
          ...transactionData,
          shippingAddress,
          cart,
        },
      },
    });
  };

  if (cart.length === 0) {
    return (
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 lg:py-24 text-center bg-cream min-h-screen">
        <h1 className="text-3xl sm:text-4xl lg:text-5xl font-playfair-display font-semibold text-primary mb-8">
          Your Cart is Empty
        </h1>

        <p className="text-lg sm:text-xl font-montserrat text-secondary mb-10 max-w-2xl mx-auto">
          Looks like you haven’t added anything yet. Start shopping now!
        </p>

        <Link to="/shop">
          <Button className="bg-primary text-cream px-10 py-5 text-xl font-montserrat rounded-full hover:bg-primary/90 transition-all duration-300 shadow-lg hover:shadow-xl hover:-translate-y-1">
            Continue Shopping
          </Button>
        </Link>
      </div>
    );
  }

  return (
    <div ref={pageRef} className="bg-cream min-h-screen py-10 sm:py-12 lg:py-16">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <h1 className="text-3xl sm:text-4xl lg:text-5xl font-playfair-display font-semibold text-primary mb-10 lg:mb-12 text-center tracking-tight">
          Checkout
        </h1>

        <div className="grid grid-cols-1 lg:grid-cols-5 gap-8 lg:gap-12">
          <div className="lg:col-span-3 space-y-10 lg:space-y-12">
            {!user && (
              <div className="bg-white p-6 lg:p-8 rounded-2xl shadow-xl border border-gray-100/70">
                <p className="text-lg font-montserrat text-secondary mb-4">
                  Already have an account? Save time by signing in.
                </p>

                <Button
                  onClick={() => setShowLoginModal(true)}
                  className="bg-primary text-cream px-8 py-3.5 text-lg font-montserrat rounded-full hover:bg-primary/90 transition-all duration-300 shadow-md hover:shadow-lg"
                >
                  Sign In
                </Button>
              </div>
            )}

            <div className="bg-white p-6 lg:p-8 rounded-2xl shadow-xl border border-gray-100/70">
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
                <h2 className="text-2xl font-playfair-display font-semibold text-primary">
                  Delivery Details
                </h2>

                {user && (
                  <label className="flex items-center gap-3 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={useAccountDetails}
                      onChange={(e) => setUseAccountDetails(e.target.checked)}
                      className="w-5 h-5 rounded border-gray-300 text-primary focus:ring-primary/40"
                    />

                    <span className="text-base font-montserrat text-secondary">
                      Use my account details
                    </span>
                  </label>
                )}
              </div>

              {user && useAccountDetails ? (
                <div className="p-5 rounded-xl bg-green-50 border border-green-200">
                  <p className="text-green-800 font-medium mb-3">
                    Using your account delivery details
                  </p>

                  <div className="text-sm text-green-700 space-y-1">
                    <p>
                      <strong>Name:</strong> {user.tenant?.full_name || user.name}
                    </p>
                    <p>
                      <strong>Email:</strong> {user.tenant?.email || user.email}
                    </p>
                    <p>
                      <strong>Phone:</strong>{' '}
                      {user.tenant?.telephone || 'Not provided'}
                    </p>
                    <p>
                      <strong>Address:</strong>{' '}
                      {user.tenant?.address || 'Not provided'}
                    </p>
                    <p><strong>Country:</strong> {user.tenant?.country?.name || 'Not provided'}</p>
<p><strong>State:</strong> {user.tenant?.state?.name || 'Not provided'}</p>
                  </div>

                  {!isShippingValid && (
                    <p className="mt-4 text-sm text-amber-700 bg-amber-50 border border-amber-200 p-3 rounded-lg">
                      Your account details are incomplete. Please update your
                      profile or uncheck this option and enter delivery details
                      manually.
                    </p>
                  )}
                </div>
              ) : (
                <ShippingForm
                  userDetails={null}
                  onSubmit={handleShippingSubmit}
                />
              )}
            </div>

            <div className="bg-white p-6 lg:p-8 rounded-2xl shadow-xl border border-gray-100/70">
              <h2 className="text-2xl font-playfair-display font-semibold text-primary mb-6">
                Payment Method
              </h2>

              {!isShippingValid && (
                <p className="text-amber-700 bg-amber-50 p-4 rounded-lg mb-6 text-center font-medium">
                  Please provide valid delivery details to continue to payment.
                </p>
              )}

              <PaymentForm
                total={total}
                onPaymentSuccess={handlePaymentSuccess}
                shippingAddress={shippingAddress}
                disabled={!isShippingValid}
              />
            </div>
          </div>

          <div className="lg:col-span-2">
            <div className="bg-white rounded-2xl shadow-2xl border border-gray-100/70 p-6 lg:p-8 lg:sticky lg:top-8">
              <OrderSummary
                cart={cart}
                total={total}
                shippingAddress={shippingAddress}
                readOnly={false}
              />
            </div>
          </div>
        </div>

        {showLoginModal && <LoginModal onClose={() => setShowLoginModal(false)} />}
      </div>
    </div>
  );
}

export default Checkout;